import { SHARING } from "../../../share/shareView";
import ShareSettingsSection from "../../../share/ShareSettingsSection";
import { useEffect, useState, useRef, useMemo } from "react";

import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Elements } from "@stripe/react-stripe-js";

import CommonButton from "../../utilities/formElements/commonButton/CommonButton";
import CommonInput from "../../utilities/formElements/commonInput/CommonInput";
import {
  toastMessageError,
  toastMessageSuccess,
} from "../../utilities/commonToastMessage";

import {
  emptyLocalStorageOnLogout,
  trakingCodeGenrator,
} from "../../../util/helper";
import { disconnectSocket } from "../../../util/socket";
import storage from "../../../util/storage";
import PageViewsBreakdown from "./PageViewsBreakdown";
import {
  getSubscriptionPageViewCount,
  subscriptionPageViews,
} from "../../../util/metricsModal";
import {
  getCreditPurchaseHistory,
  CreditPurchaseHistoryItem,
} from "../../../util/creditsModel";

import {
  ISettings,
  IRadioButtonValue,
  BillingHistory,
} from "../../../Interfaces/SettingInterface";
import { IPaymentData } from "../../../Interfaces/CommonInterface";

import { subscription } from "../../../services/susbscription";
import { session } from "../../../services/session";
import { editUserDetail, resetSettings } from "../../../services/user";
import { currentUser } from "../../../services/auth";

import CommonModel from "../../commonModel/CommonModel";
import Payment from "../paymentModel/Payment";
import PaymentMethod from "../paymentModel/PaymentMethod";

import ActionType from "../../../resources/enums";
import {
  speedOptions,
  charge,
  PAGE_VIEWS_WARNING_THRESHOLD_PERCENT,
  BASE_JUMP_VISIBLE_DAYS,
  FREE_TIER_RAW_RETENTION_DAYS,
  PAID_TIER_RETENTION_DAYS,
} from "../../../constants/commonConstants";
import { getEntitlements } from "../../../util/entitlements";

import ClipIcon from "../../../assets/images/clippy.svg";
// Same real bug as Payment.tsx's own fix (2026-08-14, see util/stripe.ts's
// comment for the full story) - loadStripe() used to be called INSIDE
// this component body, recreated on every re-render, silently breaking
// the "Update Payment Method" flow's Elements context the same way it
// broke Process Upgrade elsewhere. Now imports the one shared instance.
import stripePromise from "../../../util/stripe";
import ChangePassword from "../../commonModel/ChangePassword";
import EditProfile from "../../commonModel/EditProfile";
import CancelSubscriptionModal from "../../commonModel/CancelSubscriptionModal";
import useOnlineStatus from "../../utilities/useOnlineStatus";
import Header from "../../header/Header";
import TrialFinishModel from "../../commonModel/TrialFinishModel";
import { getPageViewsRenewalDate } from "../../../util/metricsModal";
import CreditPurchaseModal from "../../commonModel/CreditPurchaseModal";
import ApplicationPreferences from "./ApplicationPreferences";
import {
  AmbientSettings,
  sameAmbient,
  sanitizeAmbient,
} from "../../../util/ambientSettings";
import ROUTES from "../../../constants/routes";
import "./Settings.scss";

/*
 * "730 days" is accurate but nobody reads a retention promise in three-digit
 * days. Anything that lands on a whole number of years is said in years; the
 * shorter free/trial window (90) stays in days, which is how customers think
 * about it at that scale.
 */
const formatRetentionWindow = (days: number): string => {
  if (days >= 365 && days % 365 === 0) {
    const years = days / 365;
    return `${years} year${years === 1 ? "" : "s"}`;
  }
  return `${days} days`;
};

// DEMO BUILD ONLY: folded away in every other build.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const DemoSettingsNote: React.ComponentType = process.env.REACT_APP_DEMO === "1" ? require("../../../demo/DemoSettingsNote").default : () => null;

const Settings = () => {
  const { t: translation } = useTranslation();
  const dispatch = useDispatch();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const authReducer = useSelector((state: any) => state?.AuthReducer);
  const { isTrialTier, isFreeTier, effectivePageViewsLimit, rawDataRetentionDays } = getEntitlements(
    authReducer?.accountRecord
  );
  // Found live 2026-08-10, right after verifying a real credit purchase
  // end-to-end: this used to read the raw plan limit directly
  // (subscriptionData.pageViewsLimit), ignoring any bonus credit balance
  // entirely - reported directly ("Shouldn't the 238 of 100k be 238 of
  // 110k?") after a real 10k-credit purchase left the usage bar still
  // showing the old 100k ceiling. getEntitlements already computes the
  // correct effectivePageViewsLimit (plan + bonus balance) - this page just
  // wasn't using it for the one place that actually needed it.
  const pageViewsLimit = effectivePageViewsLimit;
  // subscriptionPageViews (metricsModal.ts) is a plain module-level `let`,
  // not React state - reading it directly in JSX only ever shows whatever
  // it happened to be at THIS render, and never triggers a re-render when
  // getSubscriptionPageViewCount's async fetch below actually resolves.
  // That left this page showing its stale initial (undefined -> NaN) value
  // indefinitely - reported live as "the value went to 0 and the bar was
  // empty when it should show 100k used, full bar" (2026-08-06).
  // RightMenu.tsx already had the correct pattern for this exact variable
  // (local state + setPageViewsUsed in the fetch's .then()) - mirrored here.
  const [pageViewsUsed, setPageViewsUsed] = useState<number>(
    subscriptionPageViews ?? 0
  );
  // Matches RightMenu.tsx's own calculation (see
  // PAGE_VIEWS_WARNING_THRESHOLD_PERCENT in commonConstants.ts) - both
  // surfaces should agree on when a customer is "close to their limit." The
  // threshold check below still works fine on the precise (non-floored)
  // value - PAGE_VIEWS_WARNING_THRESHOLD_PERCENT is a plain number compare,
  // unaffected either way. Kept as a raw number here (not pre-formatted) so
  // the >= comparison stays a real numeric comparison; display formatting
  // (toFixed(2)) happens only at the JSX render point below.
  const pageViewsUsagePercent =
    pageViewsLimit > 0 ? (pageViewsUsed / pageViewsLimit) * 100 : 0;

  const { handleSubmit, reset, control, formState } = useForm({
    mode: "onChange",
  });

  /*
   * 🔴 The SITE tab follows the CURRENTLY SELECTED site (authData.site), the
   * same one the dashboard and the header switcher show. It is not a list of
   * every site - switching sites in the header switches this tab's subject,
   * which keeps one idea of "the site I am looking at" across the app.
   */
  // DEMO BUILD ONLY (REACT_APP_DEMO=1): the demo's Settings opens on, and offers only, the Application tab (speed, cluster spacing, ambient): the account, site and billing tabs are not part of a demo.
  const [settingstab, setSettingstab] = useState({
    siteTab: false,
    applicationTab: process.env.REACT_APP_DEMO === "1",
    billingTab: false,
    accountTab: process.env.REACT_APP_DEMO !== "1",
  });
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] =
    useState(false);
  const [boolValue, setBoolValue] = useState({
    passwordModel: false,
    deleteAcountModel: false,
    paymentModel: false,
    windowInstance: false,
    changePaymentModel: false,
    trialModel: false,
    editProfileModel: false,
    cancelSubscriptionModel: false,
  });
  const [loading, setLoading] = useState({
    savePreferenceLoading: false,
    resetSettingLoading: false,
  });
  // The saved speed, or Fast when there is none: a new account's saved speed is 0 (the backend's
  // schema default), which is none of the three choices, so no radio was selected. The state also
  // starts from the saved choice: it used to start at Fast whatever was saved, so saving any
  // OTHER preference silently put the speed back to Fast.
  const savedSpeedOption =
    speedOptions.find(
      (o) => o.value === Number(authReducer?.bubbleClusterParameters?.speed)
    ) ?? speedOptions[0];
  const [selectedSpeedOption, setSelectedSpeedOption] =
    useState<IRadioButtonValue>(savedSpeedOption);

  // Ambient life (what the dashboard does while the site is quiet). What the account holds, made valid:
  // a missing or unrecognised value reads as its default, so an account saved before these existed shows
  // the defaults rather than nothing selected (the Bubble Speed trap).
  const savedAmbient: AmbientSettings = sanitizeAmbient(
    authReducer?.bubbleClusterParameters
  );
  const [ambient, setAmbient] = useState<AmbientSettings>(savedAmbient);
  const ambientDirty = !sameAmbient(ambient, savedAmbient);

  const [trackingCode, setTrackingCode] = useState("");
  const [billingData, setBillingData] = useState<BillingHistory>();
  const [creditPurchaseHistory, setCreditPurchaseHistory] = useState<
    CreditPurchaseHistoryItem[]
  >([]);
  const [paymentMethod, setPaymentMethod] = useState<IPaymentData>();

  // Combined billing table (2026-08-10) - subscription invoices
  // (getBillingHistory) and page-view credit purchases (a separate call,
  // see creditsModel.ts's own comment on why credits can never appear in
  // the invoice list) merged into one list. Sortable columns follow the
  // exact same precedent already established for admin tables
  // (UsersList.tsx's sortColumn/sortDirection/sortableColumns/sortIndicator
  // pattern) rather than inventing a different approach for this one table.
  const combinedBillingRows = useMemo(() => {
    const invoiceRows = (billingData ?? []).map((item) => ({
      date: item.creationDate,
      // Formatting fixed 2026-08-27. Three problems, all readability:
      // the plan name ran straight into the "(" with no space; the dates
      // carried the same weight and colour as the plan name, so the eye had
      // to parse the whole string to find the thing that identifies the row;
      // and a period whose start and end are the SAME day rendered as
      // "8/27/2026 - 8/27/2026", which reads as a bug to a customer. A
      // single-day period is real (Stripe stamps both ends identically on a
      // trial-start invoice), so it is shown as one date rather than a range.
      description: (() => {
        const start = item.periodStart
          ? new Date(item.periodStart).toLocaleDateString()
          : null;
        const end = item.periodEnd
          ? new Date(item.periodEnd).toLocaleDateString()
          : null;
        const period =
          start && end ? (start === end ? start : `${start} - ${end}`) : start || end;
        return (
          <>
            {item.planName}
            {period && <span className="billing-period"> ({period})</span>}
          </>
        );
      })(),
      descriptionSort: item.planName ?? "",
      amountDisplay: `${item.amount}`,
      amountValue: Number(item.amount) || 0,
      link: item.pdfLink,
    }));

    const creditRows = creditPurchaseHistory.map((item) => ({
      date: item.purchasedAt,
      // Prefixed per direct feedback - once invoice rows and credit rows
      // sit in the same combined table, "20,000 page views" alone read as
      // ambiguous next to a subscription line; naming what it IS first
      // fixes that at a glance.
      description: `${translation(
        "page_view_credits"
      )} - ${item.pageViews.toLocaleString()} ${translation("page_views")}`,
      descriptionSort: `page view credits - ${item.pageViews} page views`,
      amountDisplay: (item.priceCents / 100).toFixed(2),
      amountValue: item.priceCents / 100,
      link: undefined,
    }));

    return [...invoiceRows, ...creditRows];
  }, [billingData, creditPurchaseHistory, translation]);

  const billingSortableColumns: {
    [key: string]: (row: (typeof combinedBillingRows)[number]) => any;
  } = {
    date: (row) => (row.date ? new Date(row.date).getTime() : 0),
    description: (row) => row.descriptionSort.toLowerCase(),
    amount: (row) => row.amountValue,
  };
  // Defaults to newest-first (date, desc) - matches the prior unsorted
  // display's implicit ordering (both source lists already come back
  // newest-first from their own RPCs), so the default view doesn't change
  // just because sorting was added.
  const [billingSortColumn, setBillingSortColumn] = useState<string | null>(
    "date"
  );
  const [billingSortDirection, setBillingSortDirection] = useState<
    "asc" | "desc"
  >("desc");

  const handleBillingSort = (column: string) => {
    if (billingSortColumn === column) {
      setBillingSortDirection(billingSortDirection === "asc" ? "desc" : "asc");
    } else {
      setBillingSortColumn(column);
      setBillingSortDirection("asc");
    }
  };

  const sortedBillingRows = billingSortColumn
    ? [...combinedBillingRows].sort((a, b) => {
        const getValue = billingSortableColumns[billingSortColumn];
        const valueA = getValue(a);
        const valueB = getValue(b);
        if (valueA < valueB) return billingSortDirection === "asc" ? -1 : 1;
        if (valueA > valueB) return billingSortDirection === "asc" ? 1 : -1;
        return 0;
      })
    : combinedBillingRows;

  const billingSortIndicator = (column: string) => {
    if (billingSortColumn !== column) {
      return "";
    }
    return billingSortDirection === "asc" ? " ▲" : " ▼";
  };
  const [isAccountSuspended, setIsAccountSuspended] = useState(true);

  const isOnline = useOnlineStatus();
  const siteId = authReducer.authData.site._id;
  // used to check if a preference has changed: the form's own fields, or any Ambient life choice
  const isFormDirty = formState.isDirty || ambientDirty;
  const DEFAULT_D3CIRCLE_CONSTRAINT = {
    gravity: authReducer?.bubbleClusterParameters.gravity,
    charge: authReducer?.bubbleClusterParameters.charge,
    historicalCharge: authReducer?.bubbleClusterParameters.historicalCharge,
    friction: authReducer?.bubbleClusterParameters.friction,
    speed: authReducer?.bubbleClusterParameters.speed,
  };
  const [closed, setClosed] = useState<boolean>(true);
  const [chargePreviewRange, setChargePreviewRange] = useState();
  const [historicalChargePreviewRange, setHistoricalChargePreviewRange] = useState<number | undefined>();

  // Extracted so it can be re-run after a credit purchase too, not just on
  // mount - see CreditPurchaseModal.tsx's own onSuccess comment for the
  // full bug this closes (limit/balance updated live via Redux already,
  // this used-count specifically did not, until now).
  // The renewal date is shown on EVERY tier, including free and trial. It used
  // to be hidden for both, so a Base Jump customer with a monthly allowance had
  // no way to know when it reset. Chris, directly: "users must know when their
  // renewal period is."
  const [pageViewsRenewalDate, setPageViewsRenewalDate] = useState<string | null>(null);

  const refreshPageViewsUsed = () => {
    getSubscriptionPageViewCount(authReducer.authData._id).then(() => {
      setPageViewsUsed(subscriptionPageViews);
    });
    getPageViewsRenewalDate(authReducer.authData._id).then((d: any) =>
      setPageViewsRenewalDate(d ?? null)
    );
  };

  useEffect(() => {
    refreshPageViewsUsed();
    getTrackingCode(siteId);
    fetchData();
    getPaymentMethod();
  }, []);

  // The tracking-connected badge reads authReducer.authData.flags -
  // authData is otherwise only ever populated once, at login (see
  // authReducer.tsx's LOGIN case), so a connection that happens for the
  // first time while this page is already open would never be reflected
  // without a full reload/re-login. Refetching the current user record
  // (same RPC/dispatch shape the real login flow already uses) on mount and
  // periodically while this page stays open keeps the badge live instead of
  // frozen at whatever it was when the page first loaded.
  useEffect(() => {
    const refreshTrackingStatus = async () => {
      const freshUserData: any = await currentUser();
      if (freshUserData?.data) {
        dispatch({
          type: ActionType.LOGIN,
          payload: freshUserData.data,
        });
      }
    };
    refreshTrackingStatus();
    const intervalId = setInterval(refreshTrackingStatus, 30000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    fetchData();
  }, [boolValue.paymentModel]);
  useEffect(() => {
    setClosed(false);
  }, [authReducer?.isClose]);

  /**
   * to copy TRACKING CODE
   */
  const handleCopy = () => {
    if (textAreaRef.current) {
      textAreaRef.current.select();
      document.execCommand("copy");
    }
  };

  const getTrackingCode = (siteId: string) => {
    const trackingCodeData = trakingCodeGenrator(siteId);
    setTrackingCode(trackingCodeData);
  };

  /**
   *
   * save preference
   */
  const onSubmit = async (data: ISettings) => {
    setLoading({
      ...loading,
      savePreferenceLoading: true,
    });

    const result = await editUserDetail({
      userId: authReducer.authData._id,
      settings: {
        gravity: DEFAULT_D3CIRCLE_CONSTRAINT.gravity,
        speed: selectedSpeedOption.value,
        charge:-(data.charge),
        historicalCharge:-(data.historicalCharge),
        friction: data.friction,
        // Always sent WITH the bubble fields. The backend now writes each key on its own, so a partial
        // save could not wipe the other group - but a save is one action to the person, so it is one write.
        ...ambient,
      },
    });

    if (result.data) {
      toastMessageSuccess(
        translation("settings_saves_sucecesfully"),
        "settingsSaved"
      );
      dispatch({
        type: ActionType.UPDATECLUSTER,
        payload: {
          charge: result.data.settings.charge,
          historicalCharge: result.data.settings.historicalCharge,
          friction: result.data.settings.friction,
          speed: result.data.settings.speed,
          gravity: 0.2,
          ...sanitizeAmbient(result.data.settings),
        },
      });
      storage.set("bubbleParameters", {
        charge: Number(result.data.settings.charge),
        historicalCharge: Number(result.data.settings.historicalCharge),
        friction: Number(data.friction),
        speed: selectedSpeedOption.value,
        gravity: 0.2,
      });
    } else {
      toastMessageError(
        translation("something_went_wrong"),
        "settingsNotSaved"
      );
      setLoading({
        savePreferenceLoading: false,
        resetSettingLoading: false,
      });
    }
    setLoading({
      ...loading,
    });
  };

  /**
   *
   * reset settings
   */
  const handleResetSettings = async () => {
    setLoading({
      ...loading,
      resetSettingLoading: true,
    });
    const resetData = await resetSettings({ userId: authReducer.authData._id });
    if (resetData.data) {
      toastMessageSuccess(
        translation("reset_settings_succesfully"),
        "resetSettings"
      );
      reset({
        charge: resetData?.data?.settings?.charge,
        historicalCharge: resetData?.data?.settings?.historicalCharge,
        friction: resetData?.data?.settings?.friction,
        speed: resetData?.data?.settings?.speed,
      });
      dispatch({
        type: ActionType.UPDATECLUSTER,
        payload: {
          charge: resetData?.data?.settings?.charge,
          historicalCharge: resetData?.data?.settings?.historicalCharge,
          friction: resetData?.data?.settings?.friction,
          speed: resetData?.data?.settings?.speed,
          gravity: DEFAULT_D3CIRCLE_CONSTRAINT.gravity,
          ...sanitizeAmbient(resetData?.data?.settings),
        },
      });
      setAmbient(sanitizeAmbient(resetData?.data?.settings));
      // the radios are uncontrolled and Save sends THIS state: put both back to the reset speed (a reset used to leave the old choice shown and about to be re-saved)
      setSelectedSpeedOption(
        speedOptions.find((o) => o.value === Number(resetData?.data?.settings?.speed)) ?? speedOptions[0]
      );
      storage.set("bubbleParameters", {
        charge: Number(resetData?.data?.settings?.charge),
        historicalCharge: Number(resetData?.data?.settings?.historicalCharge),
        friction: Number(resetData?.data?.settings?.friction),
        speed: resetData?.data?.settings?.speed,
        gravity: 0.2,
      });
    } else {
      toastMessageError(
        translation("something_went_wrong"),
        "resetSettingError"
      );
      setLoading({
        savePreferenceLoading: false,
        resetSettingLoading: false,
      });
    }
    setLoading({
      ...loading,
    });
  };

  /**
   *
   * subscription details
   */
  const fetchData = async () => {
    const userId = authReducer.authData._id;

    const data = await subscription({
      jsonprc: "2.0",
      method: "getAccountRecord",
      params: {
        userId: authReducer.authData._id,
      },
      id: 0,
    });
    // Was fetched and discarded - this page rendered entirely from
    // authReducer.accountRecord, which is only ever set once at login, so
    // nothing on this page (plan, billing dates, usage) ever reflected a
    // plan change made after login without a full re-login. Mirrors
    // AnalyticsView.tsx's fetchSubscriptionData, which already does this.
    dispatch({
      type: ActionType.SUBSCRIPTIONDATA,
      payload: data.data?.result,
    });

    const billingHistory = await subscription({
      jsonprc: "2.0",
      method: "getBillingHistory",
      params: {
        userId: authReducer.authData._id,
      },
      id: 0,
    });
    setBillingData(billingHistory?.data?.result);

    // Separate from getBillingHistory on purpose - see creditsModel.ts's
    // own comment. A one-off credit PaymentIntent is never attached to a
    // Stripe Invoice, so it's genuinely invisible to the call above no
    // matter what; this reads our own persisted purchase record instead.
    const purchaseHistory = await getCreditPurchaseHistory(
      authReducer.authData._id
    );
    setCreditPurchaseHistory(purchaseHistory);

    getPaymentMethod();
  };

  /*
   * UNDO used to confirm NOTHING (fixed 2026-08-30, reported directly).
   *
   * It reverted the scheduled change correctly - verified on staging, the
   * pending fields cleared and the plan was retained - and then said nothing at
   * all. No toast, no message; the row simply redrew, asking the customer to
   * infer success from the absence of a notice.
   *
   * It was backwards from the rest of the same flow: SCHEDULING a downgrade,
   * which takes no money, pops a full modal, and CANCELLING pops a toast - but
   * undoing a billing decision, the action a customer is most likely to
   * second-guess, was silent. The loud confirmation was on the quieter action.
   *
   * The failure path matters more than the success one here. Without it, a
   * failed undo is indistinguishable from a successful one: the customer
   * believes they kept their plan and finds out at period end that they did
   * not.
   */
  const handleUndoCancelSubscription = async () => {
    const result = await subscription({
      jsonprc: "2.0",
      method: "undoCancelSubscription",
      params: {
        userId: authReducer.authData._id,
      },
      id: 0,
    });

    if (result && result.data && !result.data.error) {
      toastMessageSuccess(
        translation("plan_change_cancelled"),
        "planChangeCancelled"
      );
    } else {
      toastMessageError(
        translation("plan_change_cancel_failed"),
        "planChangeCancelFailed"
      );
    }

    fetchData();
  };

  const getPaymentMethod = async () => {
    const getPaymentMethod = await subscription({
      jsonprc: "2.0",
      method: "getPaymentMethod",
      params: {
        userId: authReducer.authData._id,
      },
      id: 0,
    });
    setPaymentMethod(getPaymentMethod?.data?.result);
  };

  const handleShowPreview = async (data:any) =>{
    setChargePreviewRange(data);
  };

  return (
    <div className="settings-main">
      <Header />
      {isOnline ? (
        <>
          <div className="container-fluid pt30">
            <Link
              to={
                window.location.pathname.split("/")[
                  window.location.pathname.split("/").length - 1
                ] === "settings"
                  ? ROUTES.ANALYTICS_VIEW.replace("/:id", "")
                  : ROUTES.ANALYTICS_VIEW.replace(
                      ":id",
                      window.location.pathname.split("/")[
                        window.location.pathname.split("/").length - 1
                      ]
                    )
              }
              className="link-btn"
            >
              <span>
                <svg
                  id="Layer_1"
                  height="11"
                  width="11"
                  viewBox="0 0 512 512"
                  fill="#555"
                  xmlns="http://www.w3.org/2000/svg"
                  data-name="Layer 1"
                >
                  <path d="m390.627 54.627-201.372 201.373 201.372 201.373a32 32 0 1 1 -45.254 45.254l-224-224a32 32 0 0 1 0-45.254l224-224a32 32 0 0 1 45.254 45.254z" />
                </svg>
              </span>{" "}
              {translation("back_to_main_view")}
            </Link>
            <h3>{translation("settings")}</h3>
            <div className="settings-tab-section">
              <div className="settings-tab settings-btns-wrapper ">
                <div>
                  {/* First, because the tabs read narrowest-to-widest:
                      this site -> the whole account -> the app -> billing. */}
                  {process.env.REACT_APP_DEMO !== "1" && (
                  <CommonButton
                    className={settingstab.siteTab ? "active" : ""}
                    label="Site"
                    onClick={() =>
                      setSettingstab({
                        siteTab: true,
                        applicationTab: false,
                        billingTab: false,
                        accountTab: false,
                      })
                    }
                  />
                  )}
                  {process.env.REACT_APP_DEMO !== "1" && (
                  <CommonButton
                    className={settingstab.accountTab ? "active" : ""}
                    label={translation("account")}
                    onClick={() =>
                      setSettingstab({
                        siteTab: false,
                        applicationTab: false,
                        billingTab: false,
                        accountTab: true,
                      })
                    }
                  />
                  )}
                  <CommonButton
                    className={settingstab.applicationTab ? "active" : ""}
                    label={translation("application")}
                    onClick={() =>
                      setSettingstab({
                        siteTab: false,
                        applicationTab: true,
                        billingTab: false,
                        accountTab: false,
                      })
                    }
                  />
                  {process.env.REACT_APP_DEMO !== "1" && (
                  <CommonButton
                    className={settingstab.billingTab ? "active" : ""}
                    label={translation("billing")}
                    onClick={() =>
                      setSettingstab({
                        siteTab: false,
                        applicationTab: false,
                        billingTab: true,
                        accountTab: false,
                      })
                    }
                  />
                  )}
                </div>

                {authReducer?.accountRecord?.flags?.suspended &&
                  isAccountSuspended && (
                    <>
                      <div className="account-close">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 48 48"
                        >
                          <g fill="none" fill-rule="evenodd">
                            <circle cx="24" cy="24" r="24" fill="#D86060" />
                            <path
                              fill="#F3F3F3"
                              fill-rule="nonzero"
                              d="M27.69 27.023L28.485 7h-7.11l.888 20.023h5.427zM24.977 39c1.122 0 2.074-.384 2.853-1.153.78-.768 1.17-1.68 1.17-2.734 0-1.055-.39-1.959-1.17-2.712-.78-.753-1.73-1.13-2.853-1.13-1.092 0-2.028.377-2.807 1.13-.78.753-1.17 1.657-1.17 2.712 0 1.055.39 1.966 1.17 2.734.78.769 1.715 1.153 2.807 1.153z"
                            />
                          </g>
                        </svg>
                        <p className="auth-msg danger">
                          {translation("your_account_is_currently_suspended")}
                        </p>
                        <p className="auth-msg danger">
                          {translation("your_account_is_currently_suspended")}
                        </p>
                      </div>
                    </>
                  )}

              </div>
              {/*
                THE SITE TAB - scoped to the CURRENTLY SELECTED site.

                The tracking script and its connected badge MOVED here from
                the Account tab (2026-09-27). They were always about one
                specific site, and with multi-site "the account's tracking
                script" stopped meaning anything - there is one per site.
              */}
              {settingstab.siteTab && (
                <>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="subscription-details">
                        <h4>{translation("site")}</h4>
                        <p>{authReducer.authData.site.host}</p>
                        <div className="form-group textarea-group">
                          <textarea
                            ref={textAreaRef} // Assign the ref to the textarea element
                            rows={8}
                            placeholder={translation("place_your_script_here")}
                            className="form-control"
                            defaultValue={trackingCode}
                            readOnly
                          />
                          <span className="copy-icon" onClick={handleCopy}>
                            <img src={ClipIcon} alt="ClipIcon" />

                            <div className="textarea-tooltip">
                              Copy to clipboard
                              <div className="arrow-right"></div>
                            </div>
                          </span>
                        </div>
                        <div className="tracking-status-row">
                          {/* Was a manual "Verify My Code" button (a one-time,
                              user-triggered iframe/socket check) plus a
                              separate ongoing badge derived from
                              lastActivityTimestamp's 24h staleness window -
                              meaning a site that had simply been quiet for a
                              day (not broken, just no visitors) showed as
                              "Not Connected", and organic real tracking
                              traffic never actually marked a site as
                              connected at all (only that one manual check
                              did). Replaced with a single, always-on,
                              automatic signal: socket.controller.ts's real
                              tracking handshake (not just the manual verify
                              flow) now marks trackingCodeInstalled on every
                              successful real connection, so this simply
                              reflects "have we ever received a real
                              connection from this site's tracking code,"
                              with no manual action and no recency window. */}
                          {/*
                            🔴 THE SITE'S FLAG, NOT THE USER'S (2026-09-27).
                            This read authData.flags.trackingCodeInstalled,
                            which lives on the USER - so once ANY site had
                            ever connected it was true forever, and a
                            brand-new website showed "Connected" having never
                            reported a single visitor. Exactly the wrong
                            answer for the one screen a customer checks when
                            an install is not working.
                            Site.trackingCodeInstalled is set by the real
                            tracking handshake for that specific site.
                          */}
                          <span className="tracking-status-badge">
                            <span
                              className="color-status"
                              style={{
                                backgroundColor: authReducer?.authData?.site
                                  ?.trackingCodeInstalled
                                  ? "#2ECC71"
                                  : "#D86060",
                              }}
                            />
                            {authReducer?.authData?.site?.trackingCodeInstalled
                              ? translation("connected")
                              : translation("not_connected")}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="subscription-details">
                        <h4>Page views</h4>
                        <p className="pv-breakdown-note">
                          This website's share of your account's allowance.
                        </p>
                        <PageViewsBreakdown
                          userId={authReducer?.authData?._id}
                          limit={pageViewsLimit}
                          currentSiteId={authReducer?.authData?.site?._id}
                          onlyCurrentSite
                        />
                      </div>
                    </div>
                  </div>
                  {SHARING && <ShareSettingsSection />}
                </>
              )}

              {settingstab.accountTab && (
                <>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="subscription-details">
                        <h4>{translation("subscription")}</h4>
                        <table>
                          <tr>
                            <td className="fw-500">{translation("type")}</td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {
                                authReducer?.accountRecord?.subscriptionData
                                  ?.title
                              }
                              {!isTrialTier &&
                                !!authReducer?.accountRecord?.subscriptionData
                                  ?.price && (
                                  <>
                                    {" "}
                                    <span className="separator-pipe">/</span> $
                                    {
                                      authReducer.accountRecord.subscriptionData
                                        .price
                                    }
                                    {/* Real bug found + fixed 2026-08-15: this
                                    always hardcoded "/month" regardless of
                                    the account's actual billingInterval, so
                                    an annual subscriber (who is charged
                                    `price` once a YEAR, not once a month)
                                    saw a flatly wrong cadence next to their
                                    real bill amount. */}
                                    /
                                    {authReducer.accountRecord.subscriptionData
                                      .billingInterval === "annual"
                                      ? translation("year")
                                      : translation("month")}
                                  </>
                                )}
                            </td>
                            <td className="text-end">
                              {authReducer?.accountRecord
                                ?.pendingDowngradeTitle ? (
                                <>
                                  <span className="font10 detail-small-text">
                                    {translation("downgrading_to")}{" "}
                                    {
                                      authReducer.accountRecord
                                        .pendingDowngradeTitle
                                    }{" "}
                                    {translation("on")}{" "}
                                    {new Date(
                                      authReducer.accountRecord.pendingDowngradeEffectiveDate
                                    ).toLocaleDateString()}
                                  </span>{" "}
                                  <CommonButton
                                    className="font12"
                                    style={{ textTransform: "uppercase" }}
                                    label={translation("undo")}
                                    onClick={handleUndoCancelSubscription}
                                  />
                                </>
                              ) : (
                                <>
                                  <CommonButton
                                    className="font12"
                                    style={{ textTransform: "uppercase" }}
                                    label={translation("change")}
                                    onClick={() => {
                                      dispatch({
                                        type: ActionType.SUBSCRIPTIONTITLE,
                                        payload: { title: "" },
                                      });
                                      setClosed(true);
                                      {
                                        isTrialTier
                                          ? setBoolValue({
                                              trialModel: true,
                                              paymentModel: false,
                                              passwordModel: false,
                                              deleteAcountModel: false,
                                              windowInstance: false,
                                              changePaymentModel: false,
                                              editProfileModel: false,
                                              cancelSubscriptionModel: false,
                                            })
                                          : setBoolValue({
                                              paymentModel: true,
                                              passwordModel: false,
                                              deleteAcountModel: false,
                                              windowInstance: false,
                                              changePaymentModel: false,
                                              trialModel: false,
                                              editProfileModel: false,
                                              cancelSubscriptionModel: false,
                                            });
                                      }
                                    }}
                                  />
                                  {!isTrialTier && !isFreeTier && (
                                    <>
                                      <span className="separator-pipe">
                                        {" | "}
                                      </span>
                                      <CommonButton
                                        className="font12"
                                        style={{ textTransform: "uppercase" }}
                                        label={translation("cancel")}
                                        onClick={() => {
                                          setBoolValue({
                                            passwordModel: false,
                                            deleteAcountModel: false,
                                            paymentModel: false,
                                            windowInstance: false,
                                            changePaymentModel: false,
                                            trialModel: false,
                                            editProfileModel: false,
                                            cancelSubscriptionModel: true,
                                          });
                                        }}
                                      />
                                    </>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-500">
                              {translation("page_views_used")}
                            </td>
                            <td>
                              {/*
                                🔴 THE TRIAL HAS REAL LIMITS (fixed 2026-08-27).
                                This said "Unlimited" for trial accounts, and
                                hid the usage, the percentage and the upgrade
                                prompt with it.

                                That is stale relative to a deliberate
                                decision: the trial was changed FROM unlimited
                                TO Stratosphere's numbers (300K views, 14 days)
                                because unlimited was a real cost/abuse risk
                                AND made the trial more generous than anything
                                a paying customer gets, which undercuts
                                upgrading. subscriptions.const.ts carries that
                                reasoning; this screen never followed, so a
                                trial user could be told they had no limit
                                right up until the overage ladder stopped them.

                                Found by Chris on the real Settings page.
                              */}
                              <span>{pageViewsUsed}</span>{" "}
                              <>
                                of{" "}
                                {pageViewsLimit / 1000}
                                k <span className="separator-pipe">/</span>{" "}
                                {pageViewsUsagePercent.toFixed(2)}%
                              </>
                              {pageViewsUsagePercent >=
                                PAGE_VIEWS_WARNING_THRESHOLD_PERCENT && (
                                  <>
                                    {" "}
                                    <CommonButton
                                      className="transparent-btn font12"
                                      label={translation("upgrade")}
                                      onClick={() => {
                                        dispatch({
                                          type: ActionType.SUBSCRIPTIONTITLE,
                                          payload: { title: "" },
                                        });
                                        setClosed(true);
                                        setBoolValue({
                                          paymentModel: true,
                                          passwordModel: false,
                                          deleteAcountModel: false,
                                          windowInstance: false,
                                          changePaymentModel: false,
                                          trialModel: false,
                                          editProfileModel: false,
                                          cancelSubscriptionModel: false,
                                        });
                                      }}
                                    />
                                  </>
                                )}
                              {/* Page-view credit purchase flow (2026-08-08).
                                  Still hidden on a trial, but the REASON has
                                  changed (2026-08-27): it is no longer because
                                  a trial is unlimited - it is not - but because
                                  the right action for a trial running out is to
                                  SUBSCRIBE, not to buy a top-up for a plan they
                                  do not have yet. */}
                              {!isTrialTier && (
                                <>
                                  {" "}
                                  <CommonButton
                                    className="transparent-btn font12"
                                    label={translation("buy_more_page_views")}
                                    onClick={() =>
                                      setShowCreditPurchaseModal(true)
                                    }
                                  />
                                </>
                              )}
                              {!isTrialTier &&
                                authReducer?.accountRecord
                                  ?.bonusPageViewCredits?.balance > 0 && (
                                  <div className="font10 credit-balance-note">
                                    {translation("credit_balance")}:{" "}
                                    {authReducer.accountRecord.bonusPageViewCredits.balance.toLocaleString()}
                                  </div>
                                )}
                              {/* Track div = the full bar (100%), fill div
                                  sized to the real usage percent already
                                  computed above for the "18%" text - same
                                  number, just given a visual alongside it. */}
                              {/* Shown on trials too (2026-08-27), same reason
                                  as the numbers above: the bar is only
                                  meaningless if the limit is. It is not. */}
                              {(
                                <div className="page-views-usage-bar">
                                  <div
                                    className={
                                      pageViewsUsagePercent >=
                                      PAGE_VIEWS_WARNING_THRESHOLD_PERCENT
                                        ? "page-views-usage-bar-fill danger"
                                        : "page-views-usage-bar-fill"
                                    }
                                    style={{
                                      width: `${Math.min(
                                        pageViewsUsagePercent,
                                        100
                                      )}%`,
                                    }}
                                  />
                                </div>
                              )}
                            </td>
                            <td className="font10 text-end detail-small-text">
                              {/*
                                Shown on EVERY tier (2026-08-27). This used to
                                be suppressed for free and trial accounts, so a
                                Base Jump customer with a monthly allowance had
                                no way to find out when it reset - the one fact
                                that tells them whether the number to the left
                                matters today.

                                The date comes from getPageViewsRenewalDate,
                                which is derived from the same window the usage
                                count itself is taken from, so the two can never
                                describe different periods.
                              */}
                              {pageViewsRenewalDate && (
                                <>
                                  {translation("next_pay_period")}{" "}
                                  {new Date(pageViewsRenewalDate).toLocaleDateString()}
                                </>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-500">
                              {translation("data_storage_limit")}
                            </td>
                            <td>
                              {/* Same fix as page views above - a trial keeps
                                  data for 14 days, which is a real number, not
                                  "n/a". */}
                              <>
                                {" "}
                                {
                                  authReducer?.accountRecord?.subscriptionData
                                    ?.storageDays
                                }
                                {" days"}{" "}
                              </>
                            </td>
                            <td className="font10 text-end detail-small-text">
                              {/* Shown on trials too (2026-08-27) - a trial
                                  retains data for 14 real days, so the
                                  explainer applies exactly as it does to any
                                  paid tier. Hidden previously only because the
                                  trial was assumed unlimited. */}
                              {translation("data_retention_explainer").replace(
                                "{days}",
                                formatRetentionWindow(rawDataRetentionDays)
                              )}
                            </td>
                          </tr>
                          {/* Bonus History - was a separate "danger" banner
                              (red circle-X icon, same treatment as actual
                              account suspension) sitting below this table.
                              Reusing suspension's alarm styling for what's
                              actually an upgrade opportunity, not an
                              emergency, read as broken/scary instead of
                              compelling (Chris's own direct feedback). Moved
                              into this table as a normal row, matching its
                              neighbors exactly (label / value+inline-upgrade
                              / muted note) - same visual grammar already
                              used for Page Views Used's own upgrade nudge
                              two rows up, not a bespoke component. */}
                          {authReducer?.accountRecord?.dataDeletionWarningStage !=
                            null && (
                            <tr>
                              <td className="fw-500 bonus-history-label-cell">
                                {/* Same red dot as the Settings nav item
                                    (Header.tsx's hasAccountIssue) - ties the
                                    two together visually so the nav dot
                                    reads as "something in here needs
                                    attention," not an unexplained alert.
                                    Absolutely positioned into the cell's own
                                    left padding rather than inline before
                                    the text, so "Bonus History" itself stays
                                    exactly where every other row's label
                                    starts - the dot doesn't shift the text,
                                    it sits in the gutter to its left. */}
                                <span className="settings-row-alert-dot" />
                                Bonus History
                              </td>
                              <td>
                                {/* Was leading with the raw session count
                                    ("142 sessions · expires...") - same
                                    confusion already fixed once for the
                                    banner: a bare number with no context
                                    reads as alarming/confusing before
                                    someone's even hovered the info icon to
                                    learn why it exists. Leads with the
                                    TIMING now (the concept, already
                                    established as clear on its own),
                                    session count moved to the muted note
                                    column as supporting detail for anyone
                                    who wants the specifics. */}
                                <span className="bonus-history-urgency">
                                  Expires on{" "}
                                  {authReducer.accountRecord.dataDeletionDate &&
                                    new Date(
                                      authReducer.accountRecord.dataDeletionDate
                                    ).toLocaleDateString("en-US", {
                                      month: "long",
                                      day: "numeric",
                                      year: "numeric",
                                    })}
                                </span>{" "}
                                <CommonButton
                                  className="transparent-btn font12"
                                  label={translation("upgrade")}
                                  onClick={() => {
                                    dispatch({
                                      type: ActionType.SUBSCRIPTIONTITLE,
                                      payload: { title: "" },
                                    });
                                    setClosed(true);
                                    setBoolValue({
                                      paymentModel: true,
                                      passwordModel: false,
                                      deleteAcountModel: false,
                                      windowInstance: false,
                                      changePaymentModel: false,
                                      trialModel: false,
                                      editProfileModel: false,
                                      cancelSubscriptionModel: false,
                                    });
                                  }}
                                />
                              </td>
                              <td className="font10 text-end detail-small-text">
                                {/* Icon+tooltip was dropped in favor of just
                                    stating the full explanation here - this
                                    column already exists for supporting
                                    detail (see every other row's note
                                    column), so there's no need for a
                                    separate hover-triggered UI just to hold
                                    text that fits fine at rest. Leads with
                                    the "extra days in reserve" concept
                                    (answers WHY there's more than the 30
                                    days they can see) before the specific
                                    session count, per direct wording. */}
                                Skydive keeps{" "}
                                {FREE_TIER_RAW_RETENTION_DAYS -
                                  BASE_JUMP_VISIBLE_DAYS}{" "}
                                extra days in reserve. You'll regain access
                                to{" "}
                                {authReducer.accountRecord
                                  .dataDeletionAtRiskSessionCount ?? 0}{" "}
                                sessions, plus a full {PAID_TIER_RETENTION_DAYS}{" "}
                                days going forward, if you upgrade.
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td className="fw-500">
                              {translation("activation_date")}
                            </td>
                            <td>
                              {authReducer?.accountRecord?.flags
                                .activationDate &&
                                new Date(
                                  authReducer?.accountRecord?.flags.activationDate
                                ).toLocaleDateString()}
                            </td>
                            <td className="font10 text-end detail-small-text">
                              {/* Shown regardless of current tier - a
                                  historical record of when the trial ran,
                                  even after converting to a paid plan (per
                                  explicit request 2026-07-10) - not gated to
                                  isTrialTier like it was before. */}
                              {translation("trial_period")}
                              {authReducer?.accountRecord?.createdAt &&
                                new Date(
                                  authReducer?.accountRecord?.createdAt
                                ).toLocaleDateString()}{" "}
                              -{" "}
                              {authReducer?.accountRecord?.flags
                                ?.trialFinishDate &&
                                new Date(
                                  authReducer?.accountRecord?.flags?.trialFinishDate
                                ).toLocaleDateString()}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <CommonButton
                                className="font12"
                                label={translation("logoff")}
                                onClick={() => {
                                  dispatch({
                                    type: ActionType.LOGOUT,
                                    payload: "",
                                  });
                                  disconnectSocket();
                                  emptyLocalStorageOnLogout();
                                }}
                              />
                            </td>
                          </tr>
                        </table>
                      </div>
                    </div>

                    {/*
                      IN LINE WITH SUBSCRIPTION, not below it (Chris,
                      2026-09-27). This half of the row was left empty when the
                      tracking script moved out to the Site tab, and usage
                      belongs beside the plan that sets the allowance - reading
                      "here is your plan" / "here is what you have used of it"
                      across one row rather than down the page.

                      Roll-up AND breakdown together, using the same component
                      as the switcher quick-look so the two cannot drift.
                    */}
                    <div className="col-md-6">
                      <div className="subscription-details">
                        <h4>Page views across all websites</h4>
                        <PageViewsBreakdown
                          userId={authReducer?.authData?._id}
                          limit={pageViewsLimit}
                          currentSiteId={authReducer?.authData?.site?._id}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="subscription-table">
                    <h4>{translation("users")}</h4>
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <td>{translation("name")}</td>
                            <td>{translation("email")}</td>
                            <td>{translation("password")}</td>
                            <td>{translation("role")}</td>
                            <td className="text-end"></td>
                          </tr>
                        </thead>

                        <tbody>
                          <tr>
                            <td>
                              {authReducer.authData.firstname}{" "}
                              {authReducer.authData.lastname}
                            </td>
                            <td>{authReducer.authData.email}</td>
                            <td>
                              <div className="pass-show-btn">
                                <p>*******</p>
                                <CommonButton
                                  className="transparent-btn"
                                  label={translation("change")}
                                  onClick={() => {
                                    setBoolValue({
                                      passwordModel: true,
                                      deleteAcountModel: false,
                                      paymentModel: false,
                                      windowInstance: false,
                                      changePaymentModel: false,
                                      trialModel: false,
                                      editProfileModel: false,
                                      cancelSubscriptionModel: false,
                                    });
                                  }}
                                />
                              </div>
                            </td>
                            <td>{authReducer.authData.role}</td>
                            <td className="text-end">
                              <CommonButton
                                className="transparent-btn font12"
                                label={translation("edit")}
                                onClick={() => {
                                  setBoolValue({
                                    passwordModel: false,
                                    deleteAcountModel: false,
                                    paymentModel: false,
                                    windowInstance: false,
                                    changePaymentModel: false,
                                    trialModel: false,
                                    editProfileModel: true,
                                    cancelSubscriptionModel: false,
                                  });
                                }}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="delete-row">
                      <CommonButton
                        className="transparent-btn delete-account-link"
                        label={translation("delete_account")}
                        onClick={() => {
                          setBoolValue({
                            passwordModel: false,
                            deleteAcountModel: true,
                            paymentModel: false,
                            windowInstance: false,
                            changePaymentModel: false,
                            trialModel: false,
                            editProfileModel: false,
                            cancelSubscriptionModel: false,
                          });
                        }}
                      />
                    </div>
                  </div>
                </>
              )}

              {settingstab.billingTab && (
                <>
                  <div className="row">
                    <div className="col-md-8">
                      <div className="subscription-details">
                        <h4>{translation("payment_details")}</h4>
                        <table>
                          <tr>
                            <td className="fw-500">
                              {translation("payment_method")}
                            </td>
                            <td>
                              {paymentMethod
                                ? `${paymentMethod?.cardBrand} ending in ${paymentMethod?.lastFourDigits}, exp: ${paymentMethod?.expirationMonth}/${paymentMethod?.expirationYear}`
                                : translation("have_not_set_payment_detail")}
                            </td>
                            <td>
                              <CommonButton
                                className={
                                  authReducer?.accountRecord?.flags?.suspended
                                    ? "auth-msg danger"
                                    : "transparent-btn"
                                }
                                label={"UPDATE PAYMENT DETAILS"}
                                onClick={() => {
                                  setBoolValue({
                                    passwordModel: false,
                                    deleteAcountModel: false,
                                    paymentModel: false,
                                    windowInstance: false,
                                    changePaymentModel: true,
                                    trialModel: false,
                                    editProfileModel: false,
                                    cancelSubscriptionModel: false,
                                  });
                                }}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-500">
                              {translation("billing_period")}
                            </td>
                            <td>
                              {paymentMethod
                                ? translation("thirty_days")
                                : translation("have_not_set_payment_detail")}
                            </td>
                          </tr>
                        </table>
                      </div>

                      <div className="subscription-table">
                        <div className="subscription-heading-align">
                          <h4>{translation("billing_history")}</h4>
                          <p className="header-para">
                            {translation("View_invoices_and_receipts")}
                          </p>
                        </div>
                        <div className="table-responsive">
                          <table className="table">
                            <thead>
                              <tr>
                                <td
                                  onClick={() => handleBillingSort("date")}
                                  style={{ cursor: "pointer" }}
                                >
                                  {translation("date")}
                                  {billingSortIndicator("date")}
                                </td>
                                <td
                                  colSpan={2}
                                  onClick={() =>
                                    handleBillingSort("description")
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  {translation("description")}
                                  {billingSortIndicator("description")}
                                </td>
                                <td
                                  align="right"
                                  onClick={() => handleBillingSort("amount")}
                                  style={{ cursor: "pointer" }}
                                >
                                  {translation("amount")}
                                  {billingSortIndicator("amount")}
                                </td>
                              </tr>
                            </thead>

                            <tbody>
                              {sortedBillingRows.length
                                ? sortedBillingRows.map((row, index) => (
                                    <tr key={index}>
                                      <td>
                                        {row.date &&
                                          new Date(
                                            row.date
                                          ).toLocaleDateString()}
                                      </td>
                                      <td colSpan={row.link ? 1 : 2}>
                                        {row.description}
                                      </td>
                                      {row.link && (
                                        <td align="right">
                                          <Link
                                            to={row.link}
                                            target="_blank"
                                            className="transparent-btn"
                                          >
                                            {translation("invoice")}
                                          </Link>
                                        </td>
                                      )}
                                      <td>
                                        <div className="pass-show-btn justify-content-end">
                                          <p className="m-0">
                                            $ {row.amountDisplay}
                                          </p>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                : translation("no_data_found")}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {process.env.REACT_APP_DEMO === "1" && <DemoSettingsNote />}
              {settingstab.applicationTab && (
                <form
                  onSubmit={handleSubmit((data) => onSubmit(data as ISettings))}
                >
                  <ApplicationPreferences
                    translation={translation}
                    control={control}
                    speedOptions={speedOptions}
                    savedSpeedValue={savedSpeedOption.value}
                    onSpeedChange={setSelectedSpeedOption}
                    sliderMin={-charge.max}
                    sliderMax={-charge.min}
                    defaultCharge={DEFAULT_D3CIRCLE_CONSTRAINT.charge}
                    defaultHistoricalCharge={DEFAULT_D3CIRCLE_CONSTRAINT.historicalCharge}
                    onPageChargeRelease={handleShowPreview}
                    onHistoricalChargeRelease={setHistoricalChargePreviewRange}
                    pageChargePreview={chargePreviewRange}
                    historicalChargePreview={historicalChargePreviewRange}
                    ambient={ambient}
                    onAmbientChange={setAmbient}
                    canSave={isFormDirty}
                    saving={loading.savePreferenceLoading}
                    resetting={loading.resetSettingLoading}
                    onReset={handleResetSettings}
                  />
                </form>
              )}
              {/* the demo has no Site tab: its replay clip links are listed here instead */}
            </div>
          </div>

          {boolValue.passwordModel && (
            <ChangePassword
              title={"Change Password"}
              onClose={() => {
                setBoolValue({
                  ...boolValue,
                  passwordModel: false,
                });
              }}
            />
          )}

          {boolValue.editProfileModel && (
            <EditProfile
              title={translation("edit_profile")}
              onClose={() => {
                setBoolValue({
                  ...boolValue,
                  editProfileModel: false,
                });
              }}
            />
          )}

          {boolValue.cancelSubscriptionModel && (
            <CancelSubscriptionModal
              title={translation("cancel_subscription")}
              onClose={() => {
                setBoolValue({
                  ...boolValue,
                  cancelSubscriptionModel: false,
                });
                fetchData();
              }}
            />
          )}

          {boolValue.deleteAcountModel && (
            <CommonModel
              title={"Delete Your Account"}
              onClose={() => {
                setBoolValue({
                  ...boolValue,
                  deleteAcountModel: false,
                });
              }}
              name="DeleteAccount"
            />
          )}
          {showCreditPurchaseModal && (
            <CreditPurchaseModal
              onClose={() => setShowCreditPurchaseModal(false)}
              onSuccess={refreshPageViewsUsed}
            />
          )}
          {boolValue.trialModel && closed && (
            <TrialFinishModel
              title={"Change Your Skydive Subscription"}
              onClose={() => {
                setBoolValue({
                  ...boolValue,
                  trialModel: false,
                });
              }}
              name="ChangeSub"
            />
          )}

          {boolValue.paymentModel && closed && (
            <Payment
              onClose={() => {
                setBoolValue({
                  ...boolValue,
                  paymentModel: false,
                });
              }}
              subscriptionData={authReducer?.accountRecord}
              paymentMethod={paymentMethod}
            />
          )}
          {boolValue.changePaymentModel && (
            <Elements stripe={stripePromise}>
              <PaymentMethod
                onClose={() => {
                  getPaymentMethod();

                  setBoolValue({
                    ...boolValue,
                    changePaymentModel: false,
                  });
                }}
              />
            </Elements>
          )}
        </>
      ) : (
        <h1>You are offline</h1>
      )}
    </div>
  );
};

export default Settings;

